import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import {
  Avatar,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  MenuItem as MuiMenuItem,
  styled,
} from '@mui/material';
import { AxiosError } from 'axios';
import { UserPlus } from 'lucide-react';
import { z } from 'zod';

import Button from '../../common/Button';
import {
  type TypeProject,
  type TypeProjectMember,
} from '../../common/dataTypes';
import { ProjectRolesOptions, toastPromise } from '../../common/utils';
import { useGetProjects } from '../../hooks';
import {
  useGetOrganizations,
  useGetOrgMemberById,
  useGetOrgMembers,
} from '../../hooks/tanstack/useOrganizationHook';
import {
  useCreateProjectMember,
  useUpdateProjectMember,
} from '../../hooks/tanstack/useProjectsHook';
import { useGetUser } from '../../hooks/tanstack/useUserHook';
import {
  isOrgAdmin,
  useGetRouteParams,
} from '../../hooks/tanstack/useUtilsHook';
import { type ProjectRole } from '../../types';
import { useCurrentOrganization } from '../../utils/helpers';
import CustomFormAutoComplete, {
  StyledSelect,
} from '../CustomFormAutoComplete';

const MenuItem = styled(MuiMenuItem)({
  fontSize: '14px',
});

type InviteProjectFormDialogProps = {
  open: boolean;
  inviteUserMode: 'single' | 'multiple';
  handleClose: () => void;
  handleOpen: () => void;
  project: TypeProject;
  showTrigger?: boolean;
  iProjectId?: number;
  projectMembers: TypeProjectMember[];
  iUser?: {
    id: number;
    email: string;
    fullName: string;
  };
  trigger?: {
    label: React.ReactNode;
    style?: React.CSSProperties;
  };
};

const InviteProjectFormDialog = ({
  open,
  handleClose,
  handleOpen,
  trigger,
  project,
  iUser,
  inviteUserMode,
  showTrigger = true,
  projectMembers,
}: InviteProjectFormDialogProps) => {
  const [errorMessage, setErrorMessage] = useState<React.ReactNode | null>(
    null
  );

  const form = useForm({
    defaultValues: {
      role: 'project_owner',
      email: '',
      projects: {},
      users: [],
    },
  });

  const {
    handleSubmit,
    formState,
    setError,
    reset,
    register,
    watch,
    clearErrors,
  } = form;

  const role = watch('role') as ProjectRole;

  const [searchParams] = useSearchParams();

  const { isSubmitting, errors, isDirty } = formState;

  const { orgId, userId } = useGetRouteParams();

  const type = searchParams.get('type');

  const { data: member } = useGetOrgMemberById(
    {
      orgId,
      memberId: userId,
    },
    {
      enabled: !!userId && !!orgId && type === 'member',
    }
  );

  const { data: currentUser } = useGetUser();

  const { data: organizations } = useGetOrganizations();

  const { data: orgMembers } = useGetOrgMembers(
    {
      orgId,
    },
    {
      enabled: !!orgId,
    }
  );

  const { data: currentOrg } = useCurrentOrganization();

  const { data: projects } = useGetProjects(
    {
      orgId,
    },
    {
      enabled: !!orgId,
    }
  );

  const { mutateAsync: updateProjectMember } = useUpdateProjectMember();

  const { mutateAsync: createProjectMember } = useCreateProjectMember({
    onSuccess() {
      handleCloseDialog();
      reset();
    },
    onError(error) {
      if (error instanceof AxiosError) {
        setError('email', {
          message: error.response.data['email'][0],
        });
      }
    },
  });

  const handleCloseDialog = () => {
    reset();
    setErrorMessage(null);
    handleClose();
  };

  const handleChangeRole = ({
    member,
    role,
  }: {
    member: TypeProjectMember;
    role: ProjectRole;
  }) => {
    const res = updateProjectMember({
      orgId,
      projectId: project.id,
      memberId: member.id,
      data: {
        role,
      },
    });

    toastPromise({
      promise: res,
      content: `${member.user.first_name} ${member.user.last_name} role updated to ${ProjectRolesOptions.find((p) => p.value === role)?.label}`,
    });
  };

  const formUsers: {
    id: number;
    email: string;
    avatar: string;
    fullName: string;
  }[] = watch('users');

  const email = watch('email');

  const onSubmit: SubmitHandler<any> = async () => {
    if (email) {
      const isValidEmail = z.string().email().safeParse(email);

      if (!isValidEmail.success) {
        setError('email', {
          message: 'Please enter a valid email address',
        });
        return;
      }
    }

    if (inviteUserMode === 'single') {
      if (!iUser) {
        return;
      }

      const formProjects: Record<
        string,
        {
          checked: boolean;
          role: ProjectRole;
        }
      > = watch('projects');

      const selectedProjectIds = Object.keys(formProjects).filter(
        (key) => formProjects[key].checked
      );

      if (!selectedProjectIds.length) {
        setErrorMessage('You must select at least one project');
        return;
      }

      const res = Promise.all(
        selectedProjectIds.map((key) =>
          createProjectMember({
            orgId,
            projectId: Number(key),
            data: {
              email: iUser.email,
              user: iUser.id ?? userId,
              role: formProjects[key].role,
              project: Number(key),
            },
          })
        )
      );

      toastPromise({
        promise: res,
        content: 'User added to project(s) successfully',
      });

      return;
    }

    if (inviteUserMode === 'multiple') {
      if (!role) {
        setErrorMessage('You must select a role');
        return;
      }

      if (!formUsers.length) {
        if (email) {
          setErrorMessage(
            <p>
              This user's email is not in this organization, make sure to invite
              them to the{' '}
              <Button
                variant="link_blue"
                onClick={() => {
                  if (window) {
                    window
                      .open(
                        `/${currentOrg?.id}/user-management?add=true`,
                        '_blank'
                      )
                      ?.focus();
                    handleCloseDialog();
                  }
                }}
              >
                organization
              </Button>{' '}
              first
            </p>
          );
          return;
        }

        setErrorMessage('You must select at least one email');
        return;
      }

      const res = Promise.all(
        formUsers.map((u) => {
          createProjectMember({
            orgId,
            projectId: project.id,
            data: {
              email: u.email,
              user: u.id,
              project: project.id,
              role,
            },
          });
        })
      );

      toastPromise({
        promise: res,
        content: 'User(s) added to project(s) successfully',
      });

      return;
    }
  };

  const projectTitle = project?.name ?? 'project(s)';

  const memberTitle = member
    ? member.user.first_name + ' ' + member.user.last_name
    : 'member(s)';

  const members = useMemo(
    () =>
      orgMembers?.filter(
        (o) => !projectMembers?.find((p) => p.user.id === o.user.id)
      ),
    [projectMembers, orgMembers]
  );

  const options = useMemo(
    () =>
      members?.map((m) => ({
        id: m.user.id,
        fullName: `${m.user.first_name} ${m.user.last_name}`,
        avatar: m.user.profile_pic,
        email: m.user.email,
      })) ?? [],
    [members]
  );

  useEffect(() => {
    setErrorMessage(null);
    clearErrors();
  }, [watch('email')]);

  return (
    <>
      {showTrigger && (
        <button
          type="button"
          onClick={handleOpen}
          style={{
            color: 'white',
            backgroundColor: '#B8341B',
            display: 'flex',
            gap: 4,
            borderRadius: 4,
            padding: '6px 12px',
            ...trigger?.style,
          }}
        >
          {trigger?.label ?? (
            <div className="flex w-fit items-center gap-2">
              <UserPlus size={20} />
              <p className="w-fit">Add members</p>
            </div>
          )}
        </button>
      )}
      <Dialog
        open={open}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiPaper-root': {
            overflowY: inviteUserMode === 'single' ? 'auto' : 'visible',
          },
        }}
      >
        <DialogTitle className="w-full">
          <h2 className="pt-2 text-2xl font-bold">
            Add {memberTitle} to{' ' + projectTitle}
          </h2>
        </DialogTitle>
        <DialogContent
          style={{
            overflowY: 'visible',
          }}
        >
          <div>
            <FormProvider {...form}>
              <form className="mb-2 space-y-6">
                <div className="flex flex-col gap-2">
                  <CustomFormAutoComplete
                    name="users"
                    inputName="email"
                    disabled={inviteUserMode === 'single'}
                    placeholder="Type or select emails"
                    emptyLabel="No members found in this organization"
                    defaultValue={
                      iUser && {
                        id: iUser.id,
                        fullName: iUser.fullName,
                        email: iUser.email,
                        avatar: '',
                      }
                    }
                    errorMessage={
                      errors['email']?.message || (errorMessage as string)
                    }
                    selectFieldName="role"
                    options={options}
                  />
                </div>
                {!project.id && (
                  <div className="px-3">
                    <FormGroup>
                      {projects
                        ?.filter(
                          (p) =>
                            !member?.project_memberships
                              .map((pr) => pr.project.id)
                              .includes(p.id)
                        )
                        ?.map((project) => (
                          <FormControlLabel
                            style={{
                              width: '100%',
                            }}
                            key={project.id}
                            sx={{
                              '.MuiFormControlLabel-label': {
                                width: '100%',
                              },
                            }}
                            control={
                              <Checkbox
                                {...register(
                                  `projects.${project.id}.checked` as any
                                )}
                              />
                            }
                            label={
                              <div className="flex !w-full justify-between items-center">
                                <p>{project.name}</p>
                                <StyledSelect
                                  {...register(
                                    `projects.${project.id}.role` as any
                                  )}
                                  defaultValue={ProjectRolesOptions[0].value}
                                  size="small"
                                  placeholder="Please select"
                                >
                                  {ProjectRolesOptions.map((option) => (
                                    <MenuItem
                                      key={option.value}
                                      value={option.value}
                                    >
                                      <span className="capitalize text-sm">
                                        {option.label}
                                      </span>
                                    </MenuItem>
                                  ))}
                                </StyledSelect>
                              </div>
                            }
                          />
                        ))}
                    </FormGroup>
                  </div>
                )}
                {projectMembers?.map((m) => {
                  const currentOrgMembership = organizations?.find(
                    (o) => o.id === orgId
                  );

                  const isAllowEdit =
                    currentUser?.id !== m.user.id &&
                    currentOrgMembership &&
                    isOrgAdmin(currentOrgMembership.role);

                  return (
                    <div
                      key={m.id}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <Avatar
                          src={m.user.profile_pic}
                          alt="member avatar"
                          sx={{
                            height: 40,
                            width: 40,
                            rounded: 'full',
                          }}
                        />
                        <div className="flex flex-col">
                          <label className="font-semibold">
                            {m.user.first_name} {m.user.last_name}
                          </label>
                          <p className="text-xs text-[#666]">{m.user.email}</p>
                        </div>
                      </div>
                      {isAllowEdit ? (
                        <StyledSelect
                          id="demo-simple-select"
                          size="small"
                          defaultValue={m.role}
                          onChange={(e) =>
                            handleChangeRole({
                              member: m,
                              role: e.target.value as ProjectRole,
                            })
                          }
                        >
                          <MenuItem value="project_owner">
                            Project Owner
                          </MenuItem>
                          <MenuItem value="project_member">
                            Project Member
                          </MenuItem>
                        </StyledSelect>
                      ) : (
                        <div className="px-4 text-sm">
                          {
                            ProjectRolesOptions.find(
                              ({ value }) => value === m.role
                            )?.label
                          }
                        </div>
                      )}
                    </div>
                  );
                })}
                <div className="flex-end flex justify-end gap-3">
                  <Button
                    onClick={handleCloseDialog}
                    variant="outlined"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmit((event) => onSubmit(event))}
                    loading={isSubmitting}
                    disabled={isSubmitting || !!errorMessage}
                    variant="contained_blue"
                  >
                    Add
                  </Button>
                </div>
              </form>
            </FormProvider>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InviteProjectFormDialog;
